/**
 * LightMeshPreset — Additive-blended emissive materials for light signature meshes.
 *
 * Used by LightSignatureManager to replace the original GLB light-mesh
 * materials with internally controlled emissive presets.
 *
 * Rendering strategy (per LIGHT_SIGNATURE_GUIDE.md):
 *   - `AdditiveBlending`   → light colour added on top of glass; order-independent
 *   - `depthWrite: false`  → no z-buffer write (renderOrder handles sorting)
 *   - `fog: false`         → Fog dims additive materials → invisible; always skip
 *   - `polygonOffset`      → depth bias to win against coplanar housing geometry
 *   - `emissiveIntensity: 0.0` → start in OFF state (Additive + 0 = transparent)
 *   - Subtle `color` (diffuse) per variant → uniform OFF-state presence that
 *     is NOT modulated by emissiveMap, so no signature leaks through.
 *   - Mesh renderOrder is set to 10 by LightSignatureManager so light renders
 *     AFTER the glass at renderOrder 5.
 *
 * The position-light texture is applied via `emissiveMap` + `alphaMap`
 * after async loading by LightSignatureManager.
 *
 * @see packages/engine/src/core/LightSignatureManager.ts
 * @see LIGHT_SIGNATURE_GUIDE.md
 */

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Shared base settings
// ---------------------------------------------------------------------------

const BASE_SETTINGS: Partial<THREE.MeshStandardMaterialParameters> = {
  color: new THREE.Color(0x000000), // overridden per variant below
  roughness: 0.85, // high roughness = no specular hotspots from env-map (guide value)
  metalness: 0.0, // non-metallic = no env-map contribution when OFF; avoids LED dot bleeding
  emissiveIntensity: 0.0, // OFF state: invisible with additive
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  transparent: true,
  fog: false,
  polygonOffset: true,
  polygonOffsetFactor: -4,
  polygonOffsetUnits: -4,
};

// ---------------------------------------------------------------------------
// White light mesh — front DRL / position light
// ---------------------------------------------------------------------------

/**
 * Creates the white emissive material for front light meshes.
 *
 * Emissive colour: white (0xFFFFFF).
 * Diffuse colour: subtle neutral grey (0x0F0F0F) — provides a uniform
 * OFF-state presence via scene lighting without leaking the emissiveMap
 * signature.
 * Starts with `emissiveIntensity: 0.0` (invisible in OFF state).
 */
export function createWhiteLightMeshMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    ...BASE_SETTINGS,
    name: 'IMS_LightMesh_White',
    color: new THREE.Color(0x0f0f0f), // subtle neutral diffuse — uniform OFF-state presence
    emissive: new THREE.Color(0xffffff),
  });
}

// ---------------------------------------------------------------------------
// Red light mesh — rear tail-light signature
// ---------------------------------------------------------------------------

/**
 * Creates the red emissive material for rear light meshes.
 *
 * Emissive colour: red (0xFF3030).
 * Diffuse colour: subtle dark red (0x0F0000) — provides a uniform
 * OFF-state presence via scene lighting without leaking the emissiveMap
 * signature.
 * Starts with `emissiveIntensity: 0.0` (invisible in OFF state).
 */
export function createRedLightMeshMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    ...BASE_SETTINGS,
    name: 'IMS_LightMesh_Red',
    color: new THREE.Color(0x0f0000), // subtle dark-red diffuse — uniform OFF-state presence
    emissive: new THREE.Color(0xff3030),
  });
}
